import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadUser = useCallback(async () => {
    // Returning from Google OAuth — let AuthCallback handle it, skip the /me check.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    const token = localStorage.getItem("krv_token");
    if (!token) {
      setUser(false);
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      localStorage.removeItem("krv_token");
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUser();
  }, [loadUser]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("krv_token", data.token);
    setUser(data.user);
    return data.user;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    return data; // { message: "verification_sent", email, detail } — no auto-login
  };

  const resendVerification = async (email) => {
    const { data } = await api.post("/auth/resend-verification", { email });
    return data;
  };

  const verifyEmail = async (token) => {
    const { data } = await api.get(`/auth/verify-email?token=${encodeURIComponent(token)}`);
    localStorage.setItem("krv_token", data.token);
    setUser(data.user);
    return data.user;
  };

  const completeGoogleSession = async (sessionId) => {
    const { data } = await api.post("/auth/google/session", { session_id: sessionId });
    localStorage.setItem("krv_token", data.token);
    setUser(data.user);
    return data.user;
  };

  const forgotPassword = async (email) => {
    const { data } = await api.post("/auth/forgot-password", { email });
    return data;
  };

  const resetPassword = async (token, password) => {
    const { data } = await api.post("/auth/reset-password", { token, password });
    localStorage.setItem("krv_token", data.token);
    setUser(data.user);
    return data.user;
  };

  const requestOtp = async (mobile) => {
    const { data } = await api.post("/auth/otp/request", { mobile });
    return data;
  };

  const verifyOtp = async (mobile, otp, name, category, acceptTerms) => {
    const { data } = await api.post("/auth/otp/verify", { mobile, otp, name, category, accept_terms: !!acceptTerms });
    localStorage.setItem("krv_token", data.token);
    setUser(data.user);
    return data;
  };

  const verifyEmailChange = async (token) => {
    const { data } = await api.get(`/auth/verify-email-change?token=${encodeURIComponent(token)}`);
    localStorage.setItem("krv_token", data.token);
    setUser(data.user);
    return data.user;
  };

  const refreshUser = async () => {
    const { data } = await api.get("/auth/me");
    setUser(data);
    return data;
  };

  const logout = () => {
    localStorage.removeItem("krv_token");
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, resendVerification, verifyEmail, verifyEmailChange, refreshUser, completeGoogleSession, forgotPassword, resetPassword, requestOtp, verifyOtp, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
